/**
 * Supplier Modal Component - Using FormModal with Unsaved Changes Detection
 * Includes: Basic Info, Addresses, Contacts
 */
import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Building2, MapPin, Phone, Plus, Trash2, Search, Star, Loader2 } from 'lucide-react';

const addressSchema = z.object({
  label: z.string().min(1, 'Label obrigatório'),
  zip_code: z.string().min(8, 'CEP inválido'),
  street: z.string().min(1, 'Rua obrigatória'),
  number: z.string().min(1, 'Número obrigatório'),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, 'Bairro obrigatório'),
  city: z.string().min(1, 'Cidade obrigatória'),
  state: z.string().min(2, 'Estado obrigatório').max(2, 'Use a sigla do estado'),
  is_primary: z.boolean().optional(),
});

const contactSchema = z.object({
  type: z.enum(['phone', 'email', 'whatsapp', 'other']),
  label: z.string().min(1, 'Label obrigatório'),
  value: z.string().min(1, 'Contato obrigatório'),
  is_primary: z.boolean().optional(),
});

const supplierSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  trading_name: z.string().optional(),
  document: z.string().optional(),
  contact_name: z.string().optional(),
  notes: z.string().optional(),
  is_active: z.boolean().default(true),
  addresses: z.array(addressSchema).optional(),
  contacts: z.array(contactSchema).optional(),
});

export default function SupplierModal({ open, onOpenChange, supplier, onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!supplier?.id;
  const [activeTab, setActiveTab] = useState('basic');
  const [searchingCep, setSearchingCep] = useState({});

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: '',
      trading_name: '',
      document: '',
      contact_name: '',
      notes: '',
      is_active: true,
      addresses: [],
      contacts: [],
    },
  });

  const { fields: addresses, append: appendAddress, remove: removeAddress, update: updateAddress } = useFieldArray({
    control,
    name: 'addresses',
  });

  const { fields: contacts, append: appendContact, remove: removeContact, update: updateContact } = useFieldArray({
    control,
    name: 'contacts',
  });

  // Reset form when modal opens or supplier changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (supplier) {
      reset({
        name: supplier.name || '',
        trading_name: supplier.trading_name || '',
        document: supplier.document || '',
        contact_name: supplier.contact_name || '',
        notes: supplier.notes || '',
        is_active: supplier.is_active ?? true,
        addresses: supplier.addresses || [],
        contacts: supplier.contacts || [],
      });
    } else {
      reset({
        name: '',
        trading_name: '',
        document: '',
        contact_name: '',
        notes: '',
        is_active: true,
        addresses: [],
        contacts: [],
      });
    }
    setActiveTab('basic');
  }, [open, supplier, reset]);

  // CEP lookup function
  const lookupCep = async (index, cep) => {
    if (!cep || cep.replace(/\D/g, '').length < 8) return;
    
    setSearchingCep(prev => ({ ...prev, [index]: true }));
    
    try {
      const cleanCep = cep.replace(/\D/g, '');
      const res = await api.get(`/utils/cep/${cleanCep}`);
      
      if (res.data) {
        const currentAddress = addresses[index];
        updateAddress(index, {
          ...currentAddress,
          zip_code: res.data.zip_code || cep,
          street: res.data.street || '',
          neighborhood: res.data.neighborhood || '',
          city: res.data.city || '',
          state: res.data.state || '',
          complement: res.data.complement || currentAddress?.complement || '',
        });
        toast.success('Endereço encontrado!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CEP não encontrado');
    } finally {
      setSearchingCep(prev => ({ ...prev, [index]: false }));
    }
  };

  // Set address as primary
  const setAddressPrimary = (index) => {
    addresses.forEach((addr, i) => {
      updateAddress(i, { ...addr, is_primary: i === index });
    });
  };

  // Set contact as primary for its type
  const setContactPrimary = (index) => {
    const targetType = contacts[index].type;
    contacts.forEach((contact, i) => {
      if (contact.type === targetType) {
        updateContact(i, { ...contact, is_primary: i === index });
      }
    });
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  const addNewAddress = () => {
    appendAddress({
      label: addresses.length === 0 ? 'Comercial' : '',
      zip_code: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      is_primary: addresses.length === 0,
    });
  };

  const addNewContact = (type = 'phone') => {
    const existingOfType = contacts.filter(c => c.type === type);
    appendContact({
      type,
      label: existingOfType.length === 0 ? 'Comercial' : '',
      value: '',
      is_primary: existingOfType.length === 0,
    });
  };

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Editar Fornecedor' : 'Novo Fornecedor'}
      description={isEditing ? 'Atualize as informações do fornecedor' : 'Preencha os dados do novo fornecedor'}
      size="lg"
      formId="supplier"
      entityId={supplier?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText="Salvar"
      cancelText="Cancelar"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Dados
          </TabsTrigger>
          <TabsTrigger value="addresses" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Endereços
            {addresses.length > 0 && <Badge variant="secondary" className="ml-1">{addresses.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Contatos
            {contacts.length > 0 && <Badge variant="secondary" className="ml-1">{contacts.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Razão Social *</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="Razão social"
                data-testid="supplier-name"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="trading_name">Nome Fantasia</Label>
              <Input
                id="trading_name"
                {...register('trading_name')}
                placeholder="Nome fantasia"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="document">CNPJ/CPF</Label>
              <Input
                id="document"
                {...register('document')}
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_name">Pessoa de Contato</Label>
              <Input
                id="contact_name"
                {...register('contact_name')}
                placeholder="Nome do contato"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Observações</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Observações sobre o fornecedor..."
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={watch('is_active')}
              onCheckedChange={(v) => setValue('is_active', v)}
            />
            <Label htmlFor="is_active">Fornecedor ativo</Label>
          </div>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {addresses.length === 0 ? 'Nenhum endereço cadastrado' : `${addresses.length} endereço(s)`}
            </p>
            <Button type="button" variant="outline" size="sm" onClick={addNewAddress}>
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>

          {addresses.length === 0 && (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <MapPin className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Clique em "Adicionar" para cadastrar endereços</p>
            </div>
          )}

          {addresses.map((address, index) => (
            <div key={address.id || index} className="p-4 border rounded-lg space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Input
                    {...register(`addresses.${index}.label`)}
                    placeholder="Label (Comercial, Depósito...)"
                    className="w-44 h-8"
                  />
                  {address.is_primary && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                      <Star className="h-4 w-4 text-primary fill-primary" />
                    </div>
                  )}
                </div>
                <div className="flex gap-1">
                  {!address.is_primary && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setAddressPrimary(index)}
                      title="Definir como principal"
                    >
                      <Star className="h-4 w-4" />
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeAddress(index)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">CEP</Label>
                  <div className="flex gap-1">
                    <Input
                      {...register(`addresses.${index}.zip_code`)}
                      placeholder="00000-000"
                      className="h-9"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => lookupCep(index, watch(`addresses.${index}.zip_code`))}
                      disabled={searchingCep[index]}
                    >
                      {searchingCep[index] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Rua</Label>
                  <Input {...register(`addresses.${index}.street`)} placeholder="Rua" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Número</Label>
                  <Input {...register(`addresses.${index}.number`)} placeholder="Nº" className="h-9" />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Complemento</Label>
                  <Input {...register(`addresses.${index}.complement`)} placeholder="Apto, Sala..." className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Bairro</Label>
                  <Input {...register(`addresses.${index}.neighborhood`)} placeholder="Bairro" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cidade</Label>
                  <Input {...register(`addresses.${index}.city`)} placeholder="Cidade" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">UF</Label>
                  <Input {...register(`addresses.${index}.state`)} placeholder="UF" maxLength={2} className="h-9" />
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {contacts.length === 0 ? 'Nenhum contato cadastrado' : `${contacts.length} contato(s)`}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addNewContact('phone')}>
                <Plus className="h-4 w-4 mr-1" />
                Telefone
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addNewContact('email')}>
                <Plus className="h-4 w-4 mr-1" />
                Email
              </Button>
            </div>
          </div>

          {contacts.length === 0 && (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <Phone className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Clique em "Telefone" ou "Email" para adicionar contatos</p>
            </div>
          )}

          {contacts.map((contact, index) => (
            <div key={contact.id || index} className="p-3 border rounded-lg flex items-center gap-3 bg-muted/30">
              <Controller
                name={`contacts.${index}.type`}
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-28 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone">Telefone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              
              <Input
                {...register(`contacts.${index}.label`)}
                placeholder="Label (Comercial, Financeiro...)"
                className="w-36 h-9"
              />

              <Input
                {...register(`contacts.${index}.value`)}
                placeholder={watch(`contacts.${index}.type`) === 'email' ? 'email@exemplo.com' : '(00) 00000-0000'}
                type={watch(`contacts.${index}.type`) === 'email' ? 'email' : 'text'}
                className="flex-1 h-9"
              />

              {contact.is_primary && (
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                  <Star className="h-4 w-4 text-primary fill-primary" />
                </div>
              )}
              
              {!contact.is_primary && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => setContactPrimary(index)}
                  title="Definir como principal"
                >
                  <Star className="h-4 w-4" />
                </Button>
              )}
              
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeContact(index)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </FormModal>
  );
}
